<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const emit = defineEmits(['remove']);
const { t } = useI18n();
const showErrors = ref(false);

const name = defineModel('name', {
  type: String,
  required: true,
});

const type = defineModel('type', {
  type: String,
  required: true,
});

const description = defineModel('description', {
  type: String,
  default: '',
});

const required = defineModel('required', {
  type: Boolean,
  default: false,
});

const paramTypeOptions = computed(() => [
  { value: 'string', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPES.STRING') },
  { value: 'number', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPES.NUMBER') },
  {
    value: 'integer',
    label: t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPES.INTEGER'),
  },
  {
    value: 'boolean',
    label: t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPES.BOOLEAN'),
  },
  { value: 'array', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPES.ARRAY') },
  { value: 'object', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPES.OBJECT') },
]);

const validationError = computed(() => {
  if (!name.value || name.value.trim() === '') {
    return 'PARAM_NAME_REQUIRED';
  }
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name.value.trim())) {
    return 'PARAM_NAME_INVALID';
  }
  if (!paramTypeOptions.value.some(option => option.value === type.value)) {
    return 'PARAM_TYPE_INVALID';
  }
  if (!description.value || description.value.trim() === '') {
    return 'PARAM_DESCRIPTION_REQUIRED';
  }
  if (description.value.length > 500) {
    return 'PARAM_DESCRIPTION_TOO_LONG';
  }
  return null;
});

const validationMessages = computed(() => ({
  PARAM_NAME_REQUIRED: t(
    'CAPTAIN.CUSTOM_TOOLS.FORM.ERRORS.PARAM_NAME_REQUIRED'
  ),
  PARAM_NAME_INVALID: t('CAPTAIN.CUSTOM_TOOLS.FORM.ERRORS.PARAM_NAME_INVALID'),
  PARAM_TYPE_INVALID: t('CAPTAIN.CUSTOM_TOOLS.FORM.ERRORS.PARAM_TYPE_INVALID'),
  PARAM_DESCRIPTION_REQUIRED: t(
    'CAPTAIN.CUSTOM_TOOLS.FORM.ERRORS.PARAM_DESCRIPTION_REQUIRED'
  ),
  PARAM_DESCRIPTION_TOO_LONG: t(
    'CAPTAIN.CUSTOM_TOOLS.FORM.ERRORS.PARAM_DESCRIPTION_TOO_LONG'
  ),
}));

watch([name, type, description, required], () => {
  showErrors.value = false;
});

const validate = () => {
  showErrors.value = true;
  return !validationError.value;
};

defineExpose({ validate });
</script>

<template>
  <li class="list-none">
    <div
      class="flex flex-col items-stretch gap-2 rounded-lg border border-n-weak bg-n-alpha-2 p-3 sm:flex-row sm:items-start"
      :class="{
        'animate-wiggle border-n-ruby-9': showErrors && validationError,
      }"
    >
      <div class="flex min-w-0 flex-1 flex-col gap-3">
        <div class="grid gap-2 sm:grid-cols-3">
          <Input
            v-model="name"
            :aria-label="t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_NAME.LABEL')"
            :placeholder="t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_NAME.PLACEHOLDER')"
            class="sm:col-span-2"
          />
          <ComboBox
            v-model="type"
            :options="paramTypeOptions"
            :aria-label="t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPE.LABEL')"
            :placeholder="t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_TYPE.PLACEHOLDER')"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
        <Input
          v-model="description"
          :aria-label="t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_DESCRIPTION.LABEL')"
          :placeholder="
            t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_DESCRIPTION.PLACEHOLDER')
          "
        />
        <label class="flex items-center gap-2 cursor-pointer">
          <Checkbox v-model="required" />
          <span class="text-sm text-n-slate-11">
            {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAM_REQUIRED.LABEL') }}
          </span>
        </label>
      </div>
      <Button
        solid
        slate
        icon="i-lucide-trash"
        :aria-label="t('CAPTAIN.CUSTOM_TOOLS.FORM.REMOVE_PARAMETER')"
        class="w-full flex-shrink-0 sm:w-auto"
        @click.stop="emit('remove')"
      />
    </div>
    <span
      v-if="showErrors && validationError"
      class="block mt-1 text-sm text-n-ruby-11"
    >
      {{ validationMessages[validationError] }}
    </span>
  </li>
</template>
